import * as React from 'react';
import { useEffect, useState } from 'react';

import { compose } from 'recompose';

import { simplifyQueryValue } from 'js/lib/queryUtils';
import { useRetracked } from 'js/lib/retracked';
import { useLocation } from 'js/lib/useRouter';

import Naptime from 'bundles/naptimejs';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import PaymentProcessorClientTokensV1 from 'bundles/naptimejs/resources/paymentProcessorClientTokens.v1';
import RazorpayButton from 'bundles/payments-common/components/RazorpayButton';
import { useGetSpecializationById } from 'bundles/payments-common/hooks/useGetSpecializationById';
import { getPartnerLogo, getProductName, getSubtotalTitle } from 'bundles/payments-common/utils/productItemUtils';
import type { RazorpayRawErrorResponse } from 'bundles/payments-common/utils/razorpayClientUtils';
import PaymentProcessorTypes from 'bundles/payments/common/PaymentProcessorTypes';
import ProductType from 'bundles/payments/common/ProductType';
import type { PartnerType } from 'bundles/payments/data/usePartner';
import { getRazorpayCallbackUrl } from 'bundles/payments/utils/razorpayUtils';
import Modal from 'bundles/phoenix/components/Modal';

import _t from 'i18n!nls/my-purchases-graphql';

import 'css!./__styles__/RazorpayUpdateSubscriptionLink';

const API_STATE = {
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
} as const;

type PropsFromCaller = {
  razorpaySubscriptionId: string;
  amount: number;
  currencyCode: string;
  productType: string;
  productItemId: string;
  isSandboxEnabled: boolean;
};

type PropsFromNaptimeRazorpay = {
  razorpayClientTokenResponse: PaymentProcessorClientTokensV1;
};

type PropsToComponent = PropsFromCaller & PropsFromNaptimeRazorpay;

export const RazorpayUpdateSubscriptionLink = ({
  razorpayClientTokenResponse,
  amount,
  currencyCode,
  isSandboxEnabled,
  productType,
  productItemId,
  razorpaySubscriptionId,
}: PropsToComponent) => {
  const [apiState, setApiState] = useState<(typeof API_STATE)[keyof typeof API_STATE] | undefined>(undefined);
  const [isRazorpayModalOpen, setIsRazorpayModalOpen] = useState<boolean>(false);
  const retrackedTrack = useRetracked();
  const { query } = useLocation();

  const { s12n } = useGetSpecializationById({
    s12nId: productItemId,
    skip: productType !== ProductType.SPECIALIZATION_SUBSCRIPTION,
  });

  const trackEvent = (trackingName: string, eventType: string, additionalData?: Record<string, string>): void => {
    const data = { razorpaySubscriptionId, ...additionalData };
    retrackedTrack({
      action: eventType,
      trackingName,
      trackingData: data,
    });
  };

  const handleRazorpayResponse = (): void => {
    // On success, the Razorpay modal closes, so show our status modal immediately
    setApiState(API_STATE.SUCCESS);
    setIsRazorpayModalOpen(false);
  };

  const handleRazorpayScriptFailure = (): void => {
    trackEvent('razorpay_script_failure', 'load');
  };

  const handleRazorpayPaymentFailure = (response: RazorpayRawErrorResponse): void => {
    const { reason: errorCode } = response.error;
    // On error, the Razorpay modal stays open, so wait for the modal to close
    setApiState(API_STATE.ERROR);
    setIsRazorpayModalOpen(true);
    trackEvent('razorpay_payment_failure', 'submit', { errorCode });
  };

  const handleRazorpayModalClose = (): void => {
    setIsRazorpayModalOpen(false);
    trackEvent('razorpay_modal_close', 'click');
  };

  const handleStatusModalClose = (): void => {
    setApiState(undefined);
  };

  useEffect(() => {
    const {
      razorpayPaymentId: _razorpayPaymentId,
      razorpaySubscriptionId: _razorpaySubscriptionId,
      razorpaySignature,
      razorpayErrorCode,
      razorpayErrorDescription,
      razorpayErrorReason,
      razorpayErrorSource,
      razorpayErrorStep,
    } = query;

    // Handle case of user coming back from callback flow
    if (_razorpaySubscriptionId && razorpaySignature) {
      handleRazorpayResponse();
    } else if (razorpayErrorReason) {
      /* eslint-disable @typescript-eslint/no-non-null-assertion */
      handleRazorpayPaymentFailure({
        error: {
          code: simplifyQueryValue(razorpayErrorCode)!,
          description: simplifyQueryValue(razorpayErrorDescription)!,
          reason: simplifyQueryValue(razorpayErrorReason)!,
          source: simplifyQueryValue(razorpayErrorSource)!,
          step: simplifyQueryValue(razorpayErrorStep)!,
          metadata: {
            subscription_id: simplifyQueryValue(_razorpaySubscriptionId)!,
            payment_id: simplifyQueryValue(_razorpayPaymentId)!,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const partner = s12n?.partners?.[0] as PartnerType;
  const isCourseraPlusSubscription = productType === ProductType.COURSERA_PLUS_SUBSCRIPTION;

  if (s12n && !s12n?.productVariant) {
    return null;
  }

  const apiKey = razorpayClientTokenResponse.definition.clientKeyId;

  return (
    <div className="rc-RazorpayUpdateSubscriptionLink">
      <RazorpayButton
        apiKey={apiKey}
        subscriptionId={razorpaySubscriptionId}
        isSubscriptionUpdate={true}
        amount={amount}
        currencyCode={currencyCode}
        title={getProductName({ s12n, isCourseraPlusSubscription })}
        description={getSubtotalTitle({ productItemId, productType })}
        logo={getPartnerLogo({ partner, isCourseraPlusSubscription })}
        callback={handleRazorpayResponse}
        callbackUrl={getRazorpayCallbackUrl({ isSandboxEnabled, isPaymentUpdate: true })}
        onScriptFail={handleRazorpayScriptFailure}
        onPaymentFail={handleRazorpayPaymentFailure}
        onModalClose={handleRazorpayModalClose}
        disabled={false}
        rootClassName="button-link"
      >
        {_t('Update card')}
      </RazorpayButton>
      {/* Only show subsequent success/error state on our modal after user closes Razorpay modal */}
      {!!apiState && !isRazorpayModalOpen ? (
        <Modal
          modalName="RazorpayUpdateSubscriptionStatusModal"
          handleClose={handleStatusModalClose}
          className="update-subscription-modal"
          allowClose={true}
        >
          <div className="modal-container">
            <p>
              {apiState === API_STATE.SUCCESS
                ? _t('Your payment method was successfully updated.')
                : _t('Sorry, there was an error processing your request.')}
            </p>
          </div>
        </Modal>
      ) : null}
    </div>
  );
};

export default compose<PropsToComponent, PropsFromCaller>(
  Naptime.createContainer<PropsFromNaptimeRazorpay, PropsFromCaller>(({ isSandboxEnabled }) => {
    const paymentProcessorId = isSandboxEnabled
      ? PaymentProcessorTypes.razorpaySandbox
      : PaymentProcessorTypes.razorpay;
    return {
      razorpayClientTokenResponse: PaymentProcessorClientTokensV1.get(paymentProcessorId),
    };
  })
)(RazorpayUpdateSubscriptionLink);
